import type { ButtonProps } from '@sravni/react-design-system/lib/Button';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { isMplPreApprovalOffer } from 'shared/lib';

import { MplOfferStepControls } from './MplOfferStepControls';
import { OfferStepControls } from './OfferStepControls';
import { OfferStepPopup } from './OfferStepPopup';
import { getOfferSteps } from './preApprovalOfferAdapters';
import { IMPLEMENTED_PREAPPROVAL_OFFER_STEPS } from './PreApprovalOfferControls.config';
import type { TPreApprovalControlOffer } from './types';
import { useApplicationStep } from './useApplicationStep';
import { useIframeStep } from './useIframeStep';

type Props = {
  offer: TPreApprovalControlOffer;
  buttonProps?: ButtonProps;
};

export const PreApprovalOfferControls: FC<Props> = (props) => {
  const { offer, buttonProps } = props;

  const normalizedSteps = useMemo(
    () => getOfferSteps(offer).filter((step) => Object.values(IMPLEMENTED_PREAPPROVAL_OFFER_STEPS).includes(step)),
    [offer],
  );
  const firstStep = normalizedSteps[0];

  const [stepIndex, setStepIndex] = useState(0);
  const [isStepFlowActive, setIsStepFlowActive] = useState(firstStep === 'application');

  useEffect(() => {
    setIsStepFlowActive(firstStep === 'application');
  }, [firstStep]);

  const currentStep = normalizedSteps[stepIndex];

  const onClosePopup = useCallback(() => {
    setIsStepFlowActive(false);
    setStepIndex(0);
  }, []);

  const goNext = useCallback(() => {
    const nextIndex = stepIndex + 1;
    const isLastStep = stepIndex + 1 >= normalizedSteps.length;

    if (isLastStep) {
      onClosePopup();
    }

    setStepIndex(() => {
      if (isLastStep) {
        return 0;
      }
      return nextIndex;
    });
  }, [onClosePopup, normalizedSteps.length, stepIndex]);

  const startStepFlow = useCallback(() => {
    setIsStepFlowActive(true);
  }, []);

  const { isApplicationSending } = useApplicationStep({
    offer,
    isEnabled: isStepFlowActive && stepIndex !== 0,
    currentStep,
    onApplicationStepSubmit: goNext,
  });

  useIframeStep({
    offer,
    currentStep,
    onNext: goNext,
    isEnabled: isStepFlowActive,
  });

  if (isMplPreApprovalOffer(offer)) {
    return (
      <>
        <MplOfferStepControls
          offer={offer}
          goNext={goNext}
          currentStep={currentStep}
          isStepFlowActive={isStepFlowActive}
          startStepFlow={startStepFlow}
          isApplicationSending={isApplicationSending}
          buttonProps={buttonProps}
        />
        <OfferStepPopup
          offer={offer}
          goNext={goNext}
          onClosePopup={onClosePopup}
          currentStep={currentStep}
          isStepFlowActive={isStepFlowActive}
        />
      </>
    );
  }

  return (
    <>
      <OfferStepControls
        offer={offer}
        goNext={goNext}
        currentStep={currentStep}
        isStepFlowActive={isStepFlowActive}
        startStepFlow={startStepFlow}
        isApplicationSending={isApplicationSending}
        buttonProps={buttonProps}
      />
      <OfferStepPopup
        offer={offer}
        goNext={goNext}
        onClosePopup={onClosePopup}
        currentStep={currentStep}
        isStepFlowActive={isStepFlowActive}
      />
    </>
  );
};
